import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BetaRequest.css';
import { getApiEndpoint } from './config';

function BetaRequest() {
    const navigate = useNavigate();
    const [reason, setReason] = useState('');
    const [surveyConsent, setSurveyConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [token, setToken] = useState('');

    useEffect(() => {
        const storedToken = localStorage.getItem('auth_token');
        if (!storedToken) {
            navigate('/login', { replace: true });
            return;
        }

        fetch(getApiEndpoint('/api/auth/verify'), {
            headers: { Authorization: `Bearer ${storedToken}` }
        })
            .then(res => res.json())
            .then(data => {
                if (!data.success || !data.user) {
                    localStorage.removeItem('auth_token');
                    localStorage.removeItem('user');
                    navigate('/login', { replace: true });
                    return;
                }

                const user = data.user;
                localStorage.setItem('user', JSON.stringify(user));

                if (user.beta_access_approved) {
                    navigate('/', { replace: true });
                    return;
                }

                if (user.beta_access_requested) {
                    navigate('/beta-pending', { replace: true });
                    return;
                }

                if (user.first_name) setFirstName(user.first_name);
                setToken(storedToken);
                setReady(true);
            })
            .catch(() => {
                navigate('/login', { replace: true });
            });
    }, [navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (reason.trim().length < 20) {
            setError('Please provide at least 20 characters.');
            return;
        }
        if (!surveyConsent) {
            setError('Please agree to the weekly survey to continue.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(getApiEndpoint('/api/beta/request'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ reason: reason.trim(), survey_consent: surveyConsent })
            });

            const data = await response.json();

            if (response.ok && data.success) {
                // Update cached user so the rest of the app knows the request is pending
                try {
                    const raw = localStorage.getItem('user');
                    if (raw) {
                        const user = JSON.parse(raw);
                        user.beta_access_requested = true;
                        localStorage.setItem('user', JSON.stringify(user));
                    }
                } catch (e) { /* ignore */ }

                navigate('/beta-pending', { replace: true });
            } else {
                setError(data.error || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!ready) return null;

    const charCount = reason.trim().length;
    const charOk = charCount >= 20;

    return (
        <div className="beta-request-page">
            <div className="beta-request-card">
                <span className="request-icon">🚀</span>

                <h1>Request Beta Access</h1>

                <p className="request-message">
                    {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'} Tell us a bit about yourself
                    and why you'd like to join the Launchway beta.
                </p>

                <form onSubmit={handleSubmit} noValidate>
                    <div className="request-field">
                        <label htmlFor="reason">
                            Why do you want access?
                        </label>
                        <textarea
                            id="reason"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder="Tell us about your job search, what you're looking for, or how Launchway could help you…"
                            rows={5}
                            maxLength={2000}
                            disabled={loading}
                        />
                        <span className={`char-count ${charOk ? 'ok' : ''}`}>
                            {charCount} / 20 minimum
                        </span>
                    </div>

                    <div className="request-consent">
                        <label className="consent-label">
                            <input
                                type="checkbox"
                                checked={surveyConsent}
                                onChange={e => setSurveyConsent(e.target.checked)}
                                disabled={loading}
                            />
                            <span>
                                I agree to complete a short weekly survey to help improve Launchway
                                during the beta period.
                            </span>
                        </label>
                    </div>

                    {error && <p className="request-error">{error}</p>}

                    <button
                        type="submit"
                        className="request-submit-btn"
                        disabled={loading || !charOk || !surveyConsent}
                    >
                        {loading ? 'Submitting…' : 'Submit Request'}
                    </button>
                </form>

                <button
                    className="request-back-btn"
                    onClick={() => navigate('/login')}
                    disabled={loading}
                >
                    Back to Login
                </button>
            </div>
        </div>
    );
}

export default BetaRequest;
